import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  KeyboardArrowDown as ChevronIcon,
  Add as AddIcon,
  NorthEast as ArrowOutIcon,
  PlaylistAdd as AssignIcon,
} from '@mui/icons-material';

/* ================================================================ */
/*  Theme tokens                                                     */
/* ================================================================ */

const CARD_BG      = '#FFFFFF';
const CARD_BORDER  = '#E5E7EB';
const CARD_DIVIDER = '#E5E7EB';
const TEXT_PRIMARY  = '#1F2937';
const TEXT_SECONDARY = '#374151';
const TEXT_MUTED    = '#9CA3AF';
const TEAL = '#1D9E75';

/* ================================================================ */
/*  Colour maps                                                      */
/* ================================================================ */

const SEVERITY_WEIGHTS = { Critical: 4, Major: 3, Minor: 2, Trivial: 1 };

const SEVERITY_COLOR = {
  Critical: '#E24B4A',
  Major:    '#EF9F27',
  Minor:    '#378ADD',
  Trivial:  '#9CA3AF',
};

const STATUS_BADGE = {
  Open:       { bg: '#FEF2F2', color: '#DC2626' },
  InProgress: { bg: '#FFFBEB', color: '#B45309' },
  Fixed:      { bg: '#F0FDF4', color: '#15803D' },
  Verified:   { bg: '#EFF6FF', color: '#1D4ED8' },
  Closed:     { bg: '#F3F4F6', color: '#6B7280' },
};

const SCOPE_STATUS_PILL = {
  Active:   { bg: '#ECFDF5', color: '#065F46' },
  Blocked:  { bg: '#FEF2F2', color: '#991B1B' },
  Merged:   { bg: '#EFF6FF', color: '#1E40AF' },
  Planning: { bg: '#FEF3C7', color: '#92400E' },
};

const SEVERITY_ORDER = ['Critical', 'Major', 'Minor', 'Trivial'];
const FILTER_OPTIONS = ['All', 'Blocked', 'Active', 'Merged'];
const MAX_VISIBLE_ISSUES = 4;
const INITIAL_CARDS = 6;

const AVATAR_PALETTE = [
  '#4299E1', '#E53E3E', '#9F7AEA', '#38B2AC',
  '#ED8936', '#48BB78', '#ED64A6', '#667EEA',
];

/* ================================================================ */
/*  Helpers                                                          */
/* ================================================================ */

function computeReadiness(issues) {
  if (!issues || issues.length === 0) return 0;
  const resolved = new Set(['Fixed', 'Verified', 'Closed']);
  let total = 0, done = 0;
  for (const i of issues) {
    const w = SEVERITY_WEIGHTS[i.severity] || 1;
    total += w;
    if (resolved.has(i.status)) done += w;
  }
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

function readinessColor(pct) {
  if (pct >= 80) return TEAL;
  if (pct >= 50) return '#D97706';
  return '#E24B4A';
}

function severityCounts(issues) {
  const c = { Critical: 0, Major: 0, Minor: 0, Trivial: 0 };
  for (const i of issues) if (c[i.severity] !== undefined) c[i.severity]++;
  return c;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function avatarColor(name) {
  return AVATAR_PALETTE[hashStr(name || '') % AVATAR_PALETTE.length];
}

function displayId(issue) {
  const raw = String(issue.id);
  return raw.startsWith('QA-') ? raw : `QA-${raw}`;
}

/* ================================================================ */
/*  SeverityBar                                                      */
/* ================================================================ */

function SeverityBar({ issues }) {
  const counts = severityCounts(issues);
  const total = issues.length;
  const label = SEVERITY_ORDER.filter(s => counts[s] > 0)
    .map(s => `${counts[s]} ${s.toLowerCase()}`).join(', ');

  if (total === 0) {
    return (
      <Box sx={{ height: 6, borderRadius: 9999, bgcolor: '#E5E7EB', width: '100%' }}
        role="img" aria-label="Severity breakdown: no issues" />
    );
  }
  return (
    <Box sx={{ display: 'flex', height: 6, borderRadius: 9999, overflow: 'hidden', width: '100%' }}
      role="img" aria-label={`Severity breakdown: ${label}`}>
      {SEVERITY_ORDER.map(sev => counts[sev] > 0 ? (
        <Box key={sev} sx={{ height: '100%', width: `${(counts[sev] / total) * 100}%`, bgcolor: SEVERITY_COLOR[sev] }} />
      ) : null)}
    </Box>
  );
}

/* ================================================================ */
/*  IssueRow                                                         */
/* ================================================================ */

function IssueRow({ issue, scopeName, onNavigateToIssues }) {
  const badge = STATUS_BADGE[issue.status] || STATUS_BADGE.Open;
  const statusLabel = issue.status === 'InProgress' ? 'In progress' : issue.status;

  return (
    <Box
      onClick={() => { if (onNavigateToIssues) onNavigateToIssues(scopeName || ''); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: '10px',
        py: '6px', px: '12px', borderRadius: '4px', cursor: 'pointer',
        '&:hover': { bgcolor: '#F8FAFC' },
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_COLOR[issue.severity] || '#6B7280', flexShrink: 0 }} />
      <Typography component="span" sx={{ fontFamily: '"SF Mono",SFMono-Regular,ui-monospace,monospace', fontSize: '0.75rem', color: TEXT_SECONDARY, flexShrink: 0, minWidth: 56 }}>
        {displayId(issue)}
      </Typography>
      <Typography component="span" sx={{ fontSize: '0.8rem', color: TEXT_PRIMARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
        {issue.title}
      </Typography>
      <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 600, px: '8px', py: '2px', borderRadius: '4px', bgcolor: badge.bg, color: badge.color, flexShrink: 0, whiteSpace: 'nowrap' }}>
        {statusLabel}
      </Box>
    </Box>
  );
}

/* ================================================================ */
/*  AssigneeAvatars                                                  */
/* ================================================================ */

function AssigneeAvatars({ issues }) {
  const unique = useMemo(() => {
    const seen = new Set();
    return issues.filter(i => {
      if (!i.assignee || seen.has(i.assignee)) return false;
      seen.add(i.assignee);
      return true;
    }).map(i => i.assignee);
  }, [issues]);

  if (unique.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: '4px' }}>
      {unique.map(name => (
        <Box key={name} title={name} sx={{
          width: 26, height: 26, borderRadius: '50%', bgcolor: avatarColor(name),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.6rem', fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>
          {getInitials(name)}
        </Box>
      ))}
    </Box>
  );
}

/* ================================================================ */
/*  ScopeCard                                                        */
/* ================================================================ */

function ScopeCard({ scope, onAssignIssues, onNavigateToIssues, onNavigateToStories }) {
  const [expanded, setExpanded] = useState(true);
  const [showAllIssues, setShowAllIssues] = useState(false);
  const readiness = useMemo(() => computeReadiness(scope.issues), [scope.issues]);
  const counts = useMemo(() => severityCounts(scope.issues), [scope.issues]);
  const pill = SCOPE_STATUS_PILL[scope.status] || SCOPE_STATUS_PILL.Planning;
  const total = scope.issues.length;
  const hiddenCount = total - MAX_VISIBLE_ISSUES;
  const visibleIssues = (expanded && !showAllIssues && hiddenCount > 0)
    ? scope.issues.slice(0, MAX_VISIBLE_ISSUES)
    : scope.issues;

  const toggle = useCallback(() => { setExpanded(p => !p); setShowAllIssues(false); }, []);
  const handleKey = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
  }, [toggle]);

  return (
    <Box sx={{
      bgcolor: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: '8px',
      borderTop: `3px solid ${pill.color}`,
      overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 240,
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      transition: 'all 150ms',
    }}>
      {/* ── Header ─────────────────────────────────── */}
      <Box
        tabIndex={0} role="button" aria-expanded={expanded}
        onClick={toggle} onKeyDown={handleKey}
        sx={{
          cursor: 'pointer', px: '16px', pt: '14px', pb: '10px', userSelect: 'none',
          '&:focus-visible': { outline: `2px solid ${TEAL}`, outlineOffset: -2 },
        }}
      >
        {/* Row 1: name + chevron */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography
            onClick={(e) => {
              e.stopPropagation();
              if (onNavigateToIssues) onNavigateToIssues(scope.name);
            }}
            sx={{
              fontWeight: 600, fontSize: '1rem', color: TEXT_PRIMARY, lineHeight: 1.3, pr: 1,
              cursor: 'pointer',
              '&:hover': { color: TEAL, textDecoration: 'underline' },
            }}
          >
            {scope.name}
          </Typography>
          <Box sx={{
            width: 28, height: 28, borderRadius: '6px', border: `1px solid ${CARD_DIVIDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <ChevronIcon sx={{
              fontSize: 18, color: TEXT_SECONDARY,
              transition: 'transform 200ms ease',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            }} />
          </Box>
        </Box>

        {/* Row 2: branch pill */}
        {scope.branch_name && (
          <Box component="span" sx={{
            display: 'inline-block', mt: '6px',
            fontFamily: '"SF Mono",SFMono-Regular,ui-monospace,monospace',
            fontSize: '12px', color: TEXT_MUTED,
            bgcolor: '#F9FAFB', borderLeft: `2px solid ${TEAL}`,
            px: '6px', py: '2px', borderRadius: '4px', lineHeight: 1.4,
          }}>
            {scope.branch_name}
          </Box>
        )}

        {/* Row 3: status pill + issue count + readiness */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px', flexWrap: 'wrap' }}>
          <Box component="span" role="status" sx={{
            fontSize: '13px', fontWeight: 500, px: '8px', py: '3px',
            borderRadius: 9999, bgcolor: pill.bg, color: pill.color, lineHeight: 1.4,
          }}>
            {scope.status || 'Planning'}
          </Box>
          <Typography sx={{ fontSize: '13px', color: TEXT_MUTED }}>
            {total} issue{total !== 1 ? 's' : ''}
          </Typography>
          <Typography sx={{
            fontSize: '0.875rem', fontWeight: 700, ml: 'auto',
            color: readinessColor(readiness),
          }}>
            {readiness}% ready
          </Typography>
        </Box>

        {/* Row 4: severity bar */}
        <Box sx={{ mt: '8px' }}>
          <SeverityBar issues={scope.issues} />
        </Box>

        {/* Row 5: Story progress */}
        {scope.story_progress && scope.story_progress.total > 0 && (
          <Box
            onClick={(e) => { e.stopPropagation(); if (onNavigateToStories) onNavigateToStories(scope.name); }}
            sx={{ mt: '10px', cursor: 'pointer', '&:hover .story-label': { color: TEAL } }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '3px' }}>
              <Typography className="story-label" sx={{ fontSize: '13px', fontWeight: 600, color: TEXT_PRIMARY, transition: 'color 150ms' }}>
                Stories
              </Typography>
              <Typography sx={{ fontSize: '13px', color: TEXT_PRIMARY }}>
                {scope.story_progress.total} total &middot; {scope.story_progress.done} done &middot; {scope.story_progress.remaining} left
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', height: 6, borderRadius: 9999, overflow: 'hidden', bgcolor: '#E5E7EB', width: '100%' }}>
              {scope.story_progress.done > 0 && (
                <Box sx={{ height: '100%', width: `${(scope.story_progress.done / scope.story_progress.total) * 100}%`, bgcolor: TEAL }} />
              )}
            </Box>
          </Box>
        )}

        {/* Row 6: Issue summary */}
        {total > 0 && (
          <Box
            onClick={(e) => { e.stopPropagation(); if (onNavigateToIssues) onNavigateToIssues(scope.name); }}
            sx={{ mt: '8px', cursor: 'pointer', '&:hover .issue-label': { color: TEAL } }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '3px' }}>
              <Typography className="issue-label" sx={{ fontSize: '13px', fontWeight: 600, color: TEXT_PRIMARY, transition: 'color 150ms' }}>
                Issues
              </Typography>
              <Typography sx={{ fontSize: '13px', color: TEXT_MUTED }}>
                {(() => { const resolved = scope.issues.filter(i => ['Fixed','Verified','Closed'].includes(i.status)).length; return `${total} total \u00b7 ${resolved} resolved \u00b7 ${total - resolved} open`; })()}
              </Typography>
            </Box>
            {(() => {
              const resolved = scope.issues.filter(i => ['Fixed','Verified','Closed'].includes(i.status)).length;
              const pct = total > 0 ? (resolved / total) * 100 : 0;
              return (
                <Box sx={{ display: 'flex', height: 6, borderRadius: 9999, overflow: 'hidden', bgcolor: '#E5E7EB', width: '100%' }}>
                  {resolved > 0 && <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: TEAL }} />}
                </Box>
              );
            })()}
          </Box>
        )}
      </Box>

      {/* ── Body (expanded by default) ────────────── */}
      {expanded && (
        <Box sx={{ borderTop: `1px solid ${CARD_DIVIDER}` }}>
          {total === 0 ? (
            <Typography sx={{ fontSize: '0.8rem', color: TEXT_SECONDARY, textAlign: 'center', py: '16px' }}>
              No issues in this scope
            </Typography>
          ) : (
            <Box sx={{ py: '4px' }}>
              {visibleIssues.map(issue => <IssueRow key={issue.id} issue={issue} scopeName={scope.name} onNavigateToIssues={onNavigateToIssues} />)}
              {!showAllIssues && hiddenCount > 0 && (
                <Box
                  component="button"
                  onClick={(e) => { e.stopPropagation(); setShowAllIssues(true); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: TEAL, fontSize: '0.78rem', fontWeight: 500,
                    px: '12px', py: '6px', width: '100%',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  +{hiddenCount} more issue{hiddenCount !== 1 ? 's' : ''}
                  <ArrowOutIcon sx={{ fontSize: 13 }} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ── Footer ─────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: '14px', py: '8px', borderTop: `1px solid ${CARD_DIVIDER}`, mt: 'auto',
      }}>
        <AssigneeAvatars issues={scope.issues} />

        {/* Severity count badges (shown only when no assignees or as fallback) */}
        {scope.issues.length > 0 && scope.issues.every(i => !i.assignee) && (
          <Box sx={{ display: 'flex', gap: '8px' }}>
            {SEVERITY_ORDER.map(sev => counts[sev] > 0 ? (
              <Box key={sev} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_COLOR[sev] }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: SEVERITY_COLOR[sev] }}>
                  {counts[sev]} {sev}
                </Typography>
              </Box>
            ) : null)}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', ml: 'auto' }}>
          {onAssignIssues && scope.id !== 'unscoped' && (
            <Box
              component="button"
              onClick={(e) => { e.stopPropagation(); onAssignIssues(scope); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: '3px',
                background: 'none', border: 'none', cursor: 'pointer', p: 0,
                fontSize: '0.72rem', color: TEAL, fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <AssignIcon sx={{ fontSize: 14 }} /> Assign issues
            </Box>
          )}
          <Box
            component="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onNavigateToIssues) onNavigateToIssues(scope.name);
            }}
            sx={{
              display: 'flex', alignItems: 'center', gap: '3px',
              background: 'none', border: 'none', cursor: 'pointer', p: 0,
              fontSize: '0.72rem', color: TEAL, fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View Issues <ArrowOutIcon sx={{ fontSize: 11 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ================================================================ */
/*  FilterBar                                                        */
/* ================================================================ */

function FilterBar({ active, onChange }) {
  return (
    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {FILTER_OPTIONS.map(opt => {
        const isActive = active === opt;
        return (
          <Box key={opt} component="button" onClick={() => onChange(opt)} sx={{
            fontSize: '0.75rem', fontWeight: 500, px: '12px', py: '4px',
            borderRadius: '9999px', border: '1px solid',
            borderColor: isActive ? '#5EEAD4' : '#E5E7EB',
            bgcolor: isActive ? '#F0FDFA' : 'transparent',
            color: isActive ? '#0F766E' : '#6B7280',
            cursor: 'pointer', outline: 'none', transition: 'all 150ms ease',
            '&:hover': { borderColor: isActive ? '#5EEAD4' : '#D1D5DB', bgcolor: isActive ? '#F0FDFA' : '#F9FAFB' },
            '&:focus-visible': { outline: `2px solid ${TEAL}`, outlineOffset: 1 },
          }}>
            {opt}
          </Box>
        );
      })}
    </Box>
  );
}

/* ================================================================ */
/*  ScopeSwimlanes (main export)                                     */
/* ================================================================ */

export default function ScopeSwimlanes({ scopes, onAddScope, onAssignIssues, onNavigateToIssues, onNavigateToStories }) {
  const [filter, setFilter] = useState('All');
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    if (filter === 'All') return scopes;
    return scopes.filter(s => s.status === filter);
  }, [scopes, filter]);

  const visible = useMemo(() => {
    if (showAll || filtered.length <= INITIAL_CARDS) return filtered;
    return filtered.slice(0, INITIAL_CARDS);
  }, [filtered, showAll]);

  if (!scopes || scopes.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: '48px', gap: '12px' }}>
        <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '4px' }}>
          <Typography sx={{ fontSize: '2rem' }}>📦</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>No scopes added yet</Typography>
        {onAddScope && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAddScope} variant="outlined"
            sx={{ textTransform: 'none', color: TEAL, borderColor: TEAL, '&:hover': { bgcolor: '#F0FDFA', borderColor: TEAL } }}>
            Add scope
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '12px', flexWrap: 'wrap', gap: 1 }}>
        <FilterBar active={filter} onChange={setFilter} />
        {onAddScope && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAddScope} variant="outlined"
            sx={{ textTransform: 'none', color: TEAL, borderColor: TEAL, '&:hover': { bgcolor: '#F0FDFA', borderColor: TEAL } }}>
            Add scope
          </Button>
        )}
      </Box>

      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: '32px' }}>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>No {filter.toLowerCase()} scopes</Typography>
          <Typography component="button" onClick={() => setFilter('All')}
            sx={{ fontSize: '0.8rem', color: TEAL, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', mt: '4px' }}>
            Clear filter
          </Typography>
        </Box>
      )}

      {filtered.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: '16px' }}>
          {visible.map(scope => <ScopeCard key={scope.id ?? 'unscoped'} scope={scope} onAssignIssues={onAssignIssues} onNavigateToIssues={onNavigateToIssues} onNavigateToStories={onNavigateToStories} />)}
          {/* Add scope placeholder tile */}
          {onAddScope && (
            <Box
              onClick={onAddScope}
              sx={{
                border: '2px dashed #D4EDE5', borderRadius: '8px', minHeight: 140,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', bgcolor: '#FFFFFF',
                '&:hover': { bgcolor: '#F8FAF9', borderColor: TEAL },
                transition: 'all 150ms',
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '6px' }}>
                <Typography sx={{ fontSize: '1.125rem', color: '#FFFFFF', lineHeight: 1, fontWeight: 600 }}>+</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500 }}>Add scope</Typography>
            </Box>
          )}
        </Box>
      )}

      {filtered.length > INITIAL_CARDS && (
        <Box sx={{ textAlign: 'center', mt: '12px' }}>
          <Button size="small" onClick={() => setShowAll(p => !p)}
            sx={{ textTransform: 'none', fontSize: '0.8rem', color: '#6B7280' }}>
            {showAll ? 'Show less' : `Show all ${filtered.length} scopes`}
          </Button>
        </Box>
      )}
    </Box>
  );
}
